/**
 * src/admin-ui/src/services/adminUsers.ts
 *
 * Administrator API client for users and balance adjustments.
 */

import axios from 'axios';
import { getAuthHeaders, getAuthHeadersWithContentType } from '../utils/auth';

const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:8001';

export interface AdminUser {
  user_id: number;
  email: string | null;
  name: string | null;
  created_at: number;
}

export interface AdminUserDetail extends AdminUser {
  balance_balls: string;
}

export interface BalanceTransaction {
  id: number;
  user_id: number;
  delta_balls: string;
  kind: 'deposit' | 'usage_charge' | 'manual_adjust';
  created_at: string;
  usage_id: number | null;
  invoice_id: number | null;
  meta: Record<string, unknown> | null;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

export interface BalanceAdjustmentResponse {
  transaction: BalanceTransaction;
  balance_balls: string;
}

export const getAdminUsers = async (
  offset = 0,
  limit = 50,
  search = '',
): Promise<PaginatedResponse<AdminUser>> => {
  const response = await axios.get<PaginatedResponse<AdminUser>>(`${ADMIN_API_URL}/admin/users`, {
    headers: await getAuthHeaders(),
    params: { offset, limit, search: search.trim() || undefined },
  });
  return response.data;
};

export const getAdminUser = async (userId: number): Promise<AdminUserDetail> => {
  const response = await axios.get<AdminUserDetail>(`${ADMIN_API_URL}/admin/users/${userId}`, {
    headers: await getAuthHeaders(),
  });
  return response.data;
};

export const getUserBalanceTransactions = async (
  userId: number,
  offset = 0,
  limit = 50,
): Promise<PaginatedResponse<BalanceTransaction>> => {
  const response = await axios.get<PaginatedResponse<BalanceTransaction>>(
    `${ADMIN_API_URL}/admin/users/${userId}/transactions`,
    {
      headers: await getAuthHeaders(),
      params: { offset, limit },
    },
  );
  return response.data;
};

export const adjustUserBalance = async (
  userId: number,
  deltaBalls: string,
  reason: string,
): Promise<BalanceAdjustmentResponse> => {
  const response = await axios.post<BalanceAdjustmentResponse>(
    `${ADMIN_API_URL}/admin/users/${userId}/balance-adjustments`,
    { delta_balls: deltaBalls, reason },
    { headers: await getAuthHeadersWithContentType() },
  );
  return response.data;
};
